/**
 * Cabinet trim on a wall elevation (reference-docs/cabinet-trim/overview.md).
 *
 * The elevation draws each cabinet where it already works out its box, so the
 * trim is drawn per CABINET: a crown (and stacking) band on top of the box, a
 * light-rail band under it, at the heights the shared pieces give
 * (`@shared-lib/cabinet-trim`) — the same pieces the 3D view draws and the
 * estimate bills. A return runs side-on to the wall and is not drawn.
 *
 * The valance over a sink (group 3c) is drawn in the gap between the two
 * uppers it hangs between, its outline from the shared record
 * (`valanceOutline`), the crown band carried across it.
 *
 * The pieces need the whole room (its runs say each cabinet's line, its
 * ceiling the gap), which the elevation host has and the renderer does not,
 * so each host adds `roomCabinetTrimPieces(room)` to its wall data. Pure.
 */
import {
  cabinetTrimPieces,
  cabinetValances,
  drawnCountedPieces,
  trimCeilingY,
  valanceOutline,
  valancePanelOutlines,
  withStructureOpenings,
} from '@shared-lib/cabinet-trim';

/**
 * Where the room's openings' geometry comes from — the hosts hold it as a
 * structure (or the viewer's data), or as a lookup of the structure's
 * openings by id — as the structure-like object `withStructureOpenings`
 * reads. A lookup's entries are every kind at once; the join resolves each
 * room copy by id whatever its kind.
 */
const windowSourceOf = (src) =>
  src instanceof Map ? { structureOpenings: [...src.values()] } : src;

/**
 * Every trim piece in a room, each valance over a sink as one more (`kind`
 * and `role` 'valance', its uppers as `carrierIds`), and each corbel and post
 * drawn on a seated island (`kind` 'corbel' / 'post', `role` 'drawn', the
 * island's back panel as its carrier) — `[]` for a room with none.
 * `windowSource` (a structure, or its openings by id) fills in the room's
 * slim window copies: a valance is never hung over a window it would cover.
 */
export function roomCabinetTrimPieces(unhydrated, windowSource = null) {
  if (!Array.isArray(unhydrated?.objects) || unhydrated.objects.length === 0)
    return [];
  const room = withStructureOpenings(unhydrated, windowSourceOf(windowSource));
  const { pieces } = cabinetTrimPieces(room, {
    ceilingY: trimCeilingY(room),
  });
  const valances = cabinetValances(room).valances.map((v) => ({
    ...v,
    kind: 'valance',
    role: 'valance',
    carrierIds: v.betweenIds,
  }));
  const drawn = drawnCountedPieces(room).pieces.map((p) => ({
    ...p,
    role: 'drawn',
    carrierIds: [p.carrierId],
  }));
  return [...pieces, ...valances, ...drawn];
}

/**
 * Where each corbel and post is drawn on an elevation: `{ key, kind, shape,
 * left, width, top, height }` in container px — only on the elevation that
 * sees its back panel's finished face (`view: 'front'`), placed along the
 * panel's drawn box from the panel's own frame (`alongM`; `aligned` says
 * whether the panel's own left-to-right runs left-to-right on screen).
 * `placeOf(id)` gives the panel's `{ left, width, pxAt, view, aligned }`, or
 * null.
 */
export function countedPieceElevationBoxes(pieces, placeOf, scale) {
  return (pieces || [])
    .filter((p) => p.role === 'drawn')
    .flatMap((p) => {
      const box = placeOf(p.carrierId);
      if (!box || box.view !== 'front') return [];
      const mid = box.left + box.width / 2;
      const x = mid + (box.aligned ? 1 : -1) * p.alongM * scale;
      const width = p.dimensions.x * scale;
      return [
        {
          key: p.id,
          kind: p.kind,
          shape: p.shape ?? null,
          left: x - width / 2,
          width,
          top: box.pxAt(p.center.y + p.dimensions.y / 2),
          height: p.dimensions.y * scale,
        },
      ];
    });
}

// the bands a valance carries across its gap
const GAP_BAND_KINDS = new Set(['crown', 'stacking']);

/** A valance outline in its box's px: x across `width`, y down from its top. */
const outlinePx = (v, points, width, scale) =>
  points.map(([x, y]) => [
    ((x + v.lengthM / 2) / v.lengthM) * width,
    (v.heightM / 2 - y) * scale,
  ]);

/**
 * Where each valance is drawn on an elevation: `{ key, style, left, width,
 * top, height, outline, panels, bands }` in container px, the outline and
 * panels in the box's own px. Drawn only where both its uppers are drawn
 * front-on — `placeOf(id)` gives an upper's `{ left, width, pxAt(worldY) }`,
 * or null — and the bands both carry (`cabinetTrimBandsByCarrier`) run
 * across the gap.
 */
export function valanceElevationBoxes(pieces, placeOf, bandsByCarrier, scale) {
  return (pieces || [])
    .filter((p) => p.kind === 'valance')
    .flatMap((v) => {
      const [a, b] = v.carrierIds.map(placeOf);
      if (!a || !b) return [];
      const [left, right] =
        a.left < b.left
          ? [a.left + a.width, b.left]
          : [b.left + b.width, a.left];
      const width = right - left;
      if (!(width > 0)) return [];
      const topY = v.center.y + v.heightM / 2;
      const aBands = bandsByCarrier.get(v.carrierIds[0]) || {};
      const bBands = bandsByCarrier.get(v.carrierIds[1]) || {};
      return [
        {
          key: v.id,
          style: v.style,
          left,
          width,
          top: a.pxAt(topY),
          height: v.heightM * scale,
          outline: outlinePx(v, valanceOutline(v), width, scale),
          panels: valancePanelOutlines(v).map((pts) =>
            outlinePx(v, pts, width, scale)
          ),
          // only the crown (and its stacking) crosses the gap, as in 3D —
          // the valance carries the crown; the light rail stays under the
          // uppers and never spans the window
          bands: Object.entries(aBands)
            .filter(([kind]) => bBands[kind] && GAP_BAND_KINDS.has(kind))
            .map(([kind, band]) => ({
              kind,
              top: a.pxAt(band.topY),
              height: (band.topY - band.bottomY) * scale,
            })),
        },
      ];
    });
}

/**
 * The bands to draw on each cabinet, by id: `{ crown?, stacking?,
 * 'light-rail'? }`, each `{ bottomY, topY }` in world metres.
 */
export function cabinetTrimBandsByCarrier(pieces) {
  const bands = new Map();
  for (const p of pieces || []) {
    if (p.role !== 'front') continue;
    for (const id of p.carrierIds) {
      if (!bands.has(id)) bands.set(id, {});
      bands.get(id)[p.kind] = { bottomY: p.bottomY, topY: p.topY };
    }
  }
  return bands;
}
